/**
 * Auth Callback Page
 *
 * Handles OAuth callback from SuperTokens third-party providers
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Session from 'supertokens-web-js/recipe/session';
import { useAuthStore } from '../../stores/auth-store';
import { useLanguage } from '../../contexts/LanguageContext';

// Module-level flag survives component unmount/remount (React StrictMode double-mount).
// useRef(false) resets on remount, which allows duplicate OAuth calls.
let callbackProcessed = false;

export default function AuthCallback() {
  const navigate = useNavigate();
  const { handleAuthCallback } = useAuthStore();
  const { t } = useLanguage();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (callbackProcessed) return;
    callbackProcessed = true;

    const processCallback = async () => {
      try {
        await handleAuthCallback();
        // Get updated isAdmin from store after callback
        const { isAdmin: newIsAdmin } = useAuthStore.getState();
        navigate(newIsAdmin ? '/admin' : '/account/checklists');
      } catch (err) {
        // A duplicate signInAndUp call can fail with "invalid_grant" even though
        // the first call already created a valid session. If a session exists,
        // the login succeeded — navigate instead of showing error.
        if (await Session.doesSessionExist()) {
          const { isAdmin: newIsAdmin } = useAuthStore.getState();
          navigate(newIsAdmin ? '/admin' : '/account/checklists');
          return;
        }
        console.error('Auth callback failed:', err);
        setError(err instanceof Error ? err.message : 'Authentication failed');
      }
    };

    processCallback();
  }, [handleAuthCallback, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full mx-auto p-6">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">{t('auth.callbackFailed', 'Authentication Failed')}</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="w-full py-3 px-4 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors"
            >
              {t('auth.returnToLogin', 'Return to Login')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" />
        <p className="text-gray-600">{t('auth.completingSignIn', 'Completing sign in...')}</p>
      </div>
    </div>
  );
}
